// ============================================================================
// HOVER TOOLBAR - Floating toolbar that appears on mouse movement
// Allows quick preset switching and fullscreen toggle in the popup window
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PRESET_LAYOUTS } from './presets';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';

const HIDE_DELAY_MS = 3000;
const LAYOUT_KEY_PREFIX = 'second-screen-layout-';

function storageKey(prefix: string, sessionId: string, screenNumber: number): string {
  return `${prefix}${sessionId}-screen${screenNumber}`;
}

interface HoverToolbarProps {
  sessionId: string;
  screenNumber: number;
}

const HoverToolbar: React.FC<HoverToolbarProps> = ({ sessionId, screenNumber }) => {
  const { theme, toggleTheme } = useSecondScreenData();
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setVisible(false), HIDE_DELAY_MS);
  }, []);

  useEffect(() => {
    const handleMouseMove = () => show();
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [show]);

  const layoutKey = storageKey(LAYOUT_KEY_PREFIX, sessionId, screenNumber);

  const applyPreset = (presetIndex: number) => {
    const preset = PRESET_LAYOUTS[presetIndex];
    if (preset) {
      localStorage.setItem(layoutKey, JSON.stringify(preset));
      // Force re-read in same window (storage events only fire cross-window)
      window.dispatchEvent(new StorageEvent('storage', {
        key: layoutKey,
        newValue: JSON.stringify(preset),
      }));
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/70 backdrop-blur-md border border-white/10 shadow-2xl">
        {/* Preset buttons */}
        {PRESET_LAYOUTS.map((preset, index) => (
          <button
            key={preset.id}
            onClick={() => applyPreset(index)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title={preset.name}
          >
            {preset.name}
          </button>
        ))}

        <div className="w-px h-6 bg-white/20 mx-1" />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            </svg>
          )}
        </button>

        {/* Reset viewport size */}
        <button
          onClick={() => window.resizeTo(1920, 1080)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title="Reset window to 1920×1080"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
          </svg>
        </button>

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title="Toggle fullscreen"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default HoverToolbar;
